import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { HttpService } from './http.service';
import { UserService } from './user.service';

/** Known keys sent by backend in notification.data (order_created, order_canceled, etc.). */
export interface NotificationData {
  orderId?: string;
  orderNumber?: string;
  status?: string;
  /** Seller view: client name (order_created, order_canceled). */
  customerName?: string;
  /** Seller view: client phone for contact. */
  customerPhone?: string;
  /** Seller view: client email for contact. */
  customerEmail?: string;
  totalFormatted?: string;
  sellerName?: string;
  [key: string]: string | undefined;
}

export interface AppNotification {
  id: string;
  userId: string;
  recipientType: 'user' | 'seller';
  type: string;
  title: string;
  body: string;
  data?: NotificationData;
  read?: boolean;
  createdAt: any;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private unreadCountSubject = new BehaviorSubject<number>(0);
  public unreadCount$ = this.unreadCountSubject.asObservable();

  constructor(
    private httpService: HttpService,
    private userService: UserService
  ) {}

  /** Register FCM token with backend (call after login). Role: 'user' | 'seller' */
  registerFcmToken(fcmToken: string, role: 'user' | 'seller'): Promise<void> {
    const opts = this.httpService.getOpts();
    if (!opts) return Promise.resolve();
    return this.httpService
      .post('notification/register-token', { fcmToken, role }, opts)
      .toPromise()
      .then(() => {})
      .catch((err) => console.warn('Register FCM token failed', err));
  }

  /**
   * Request FCM token and register with backend.
   * - Native (iOS/Android): uses @capacitor/push-notifications (FCM/APNs).
   * - Web: uses Firebase JS Messaging (requires firebase-messaging-sw.js and vapidKey in environment).
   */
  async requestFcmTokenAndRegister(): Promise<void> {
    const user = this.userService.getCurrentUser();
    if (!user?.id) return;
    const role = user.type === 'seller' ? 'seller' : 'user';
    if (Capacitor.isNativePlatform()) {
      await this.requestNativeFcmTokenAndRegister(role);
    } else {
      await this.requestWebFcmTokenAndRegister(role);
    }
  }

  /** Native (iOS/Android): get token via Capacitor Push Notifications and register with backend. */
  private async requestNativeFcmTokenAndRegister(role: 'user' | 'seller'): Promise<void> {
    try {
      const { PushNotifications } = await import('@capacitor/push-notifications');

      // Check permissions first
      const perm = await PushNotifications.checkPermissions();
      console.log('NotificationService: current permission status:', perm.receive);

      if (perm.receive !== 'granted') {
        console.log('NotificationService: requesting push permissions...');
        const request = await PushNotifications.requestPermissions();
        console.log('NotificationService: permission request result:', request.receive);

        if (request.receive !== 'granted') {
          console.warn('NotificationService: push permission denied on native');
          return;
        }
      }

      // Add listeners BEFORE registering to avoid race conditions
      await PushNotifications.addListener(
        'registration',
        (ev: { value: string }) => {
          console.log('NotificationService: FCM token received:', ev.value?.substring(0, 20) + '...');
          if (ev?.value) {
            this.registerFcmToken(ev.value, role).catch(err => {
              console.error('NotificationService: token registration failed', err);
            });
          }
        }
      );

      await PushNotifications.addListener('registrationError', (err) => {
        console.error('NotificationService: native push registration error', err);
      });

      // Add listener for received notifications
      await PushNotifications.addListener('pushNotificationReceived', (notification) => {
        console.log('NotificationService: push notification received', notification);
      });

      // Now register for push notifications
      console.log('NotificationService: registering for push notifications...');
      await PushNotifications.register();
      console.log('NotificationService: push registration initiated successfully');

    } catch (e) {
      console.error('NotificationService: native FCM setup failed', e);
      // Don't crash the app, just log the error
    }
  }

  /** Web: get FCM token via Firebase Messaging (requires firebase-messaging-sw.js and vapidKey). */
  private async requestWebFcmTokenAndRegister(role: 'user' | 'seller'): Promise<void> {
    try {
      const { getMessaging, getToken } = await import('firebase/messaging');
      const { initializeApp } = await import('firebase/app');
      const { environment } = await import('../../environments/environment');
      const vapidKey = (environment as any).firebase?.vapidKey;
      if (!vapidKey) {
        console.warn('NotificationService: no vapidKey in environment, skip FCM registration');
        return;
      }
      const app = initializeApp((environment as any).firebase);
      const messaging = getMessaging(app);
      const token = await getToken(messaging, { vapidKey });
      if (token) await this.registerFcmToken(token, role);
    } catch (e) {
      console.warn('FCM getToken failed (e.g. no service worker or permission denied)', e);
    }
  }

  getNotifications(): Promise<AppNotification[]> {
    const opts = this.httpService.getOpts();
    if (!opts) return Promise.resolve([]);
    return this.httpService
      .get('notification', opts)
      .toPromise()
      .then((res: any) => res || [])
      .catch(() => []);
  }

  getUnreadCount(): Promise<number> {
    const opts = this.httpService.getOpts();
    if (!opts) return Promise.resolve(0);
    return this.httpService
      .get('notification/unread-count', opts)
      .toPromise()
      .then((res: any) => {
        const count = res?.count ?? 0;
        this.unreadCountSubject.next(count);
        return count;
      })
      .catch(() => 0);
  }

  setUnreadCount(count: number): void {
    this.unreadCountSubject.next(count);
  }

  markAsRead(id: string): Promise<void> {
    const opts = this.httpService.getOpts();
    if (!opts) return Promise.resolve();
    return this.httpService
      .put(`notification/${id}/read`, {}, opts)
      .toPromise()
      .then(() => { this.getUnreadCount(); })
      .catch(() => {}) as Promise<void>;
  }

  markAllAsRead(): Promise<void> {
    const opts = this.httpService.getOpts();
    if (!opts) return Promise.resolve();
    return this.httpService
      .put('notification/read-all', {}, opts)
      .toPromise()
      .then(() => {
        this.unreadCountSubject.next(0);
      })
      .catch(() => {});
  }

  /** Call after login to register push token and refresh unread count */
  onUserLoggedIn(): void {
    this.getUnreadCount();
    this.requestFcmTokenAndRegister();
  }
}
